import {
  Address,
  Box,
  BrightyListItem,
  DateTime,
  List,
  ListItem,
  Stack,
  styled,
  Typography,
} from '@razz-team/ui-web';
import { ApiTransaction } from '../../../openapi';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TransactionPartName } from './TransactionPartName';
import { CardButtonLink } from './CardButtonLink';

const ListItemValue = styled(Box)({
  maxWidth: '65%',
  textAlign: 'end',
});

function DetailsListItem({
  label,
  value,
  wordBreak = 'break-word',
}: {
  label: ReactNode;
  value: ReactNode;
  wordBreak?: 'break-word' | 'break-all';
}) {
  return (
    <Stack direction="row" spacing={2} justifyContent="space-between" component={ListItem}>
      <Typography>{label}</Typography>
      <ListItemValue sx={{ wordBreak }}>{value}</ListItemValue>
    </Stack>
  );
}

function BeneficiaryItem({ trx }: { trx: ApiTransaction }) {
  const { t } = useTranslation();

  let label: ReactNode = null;
  const beneficiary = trx.counterparty.beneficiary;
  switch (beneficiary?.type) {
    case 'CRYPTO':
      switch (beneficiary.subtype) {
        case 'MYSELF':
          label = t('Myself');
          break;
        case 'BUSINESS':
          label = beneficiary.beneficiaryName ?? t('Business');
          break;
        case 'INDIVIDUAL':
          label = beneficiary.beneficiaryName ?? t('Individual');
          break;
      }
      break;
    case 'BUSINESS':
      label = beneficiary.businessName ?? t('Business');
      break;
    case 'INDIVIDUAL':
      label = beneficiary.individualName ?? t('Individual');
      break;
  }

  return label ? <DetailsListItem label={t('Beneficiary')} value={label} /> : null;
}

export function TransactionDetails({ trx }: { trx: ApiTransaction }) {
  const { t } = useTranslation();

  const card = trx.origin.card ?? trx.counterparty.card;
  const beneficiary = trx.counterparty.beneficiary;
  const address = beneficiary?.type !== 'CRYPTO' ? beneficiary?.beneficiaryAddress : undefined;
  const category = trx.counterparty.merchant?.category ?? trx.origin.merchant?.category;
  const { link, transactionHash } = trx.counterparty.crypto ?? trx.origin.crypto ?? {};

  return (
    <List>
      <DetailsListItem label={t('Status')} value={trx.state} />
      <DetailsListItem label={t('Date, time')} value={<DateTime value={trx.date.created} />} />
      <DetailsListItem label={t('From')} value={<TransactionPartName party={trx.origin} />} />
      {card ? <DetailsListItem label={t('Card')} value={<CardButtonLink card={card} />} /> : null}
      <DetailsListItem
        label={t('To')}
        value={<TransactionPartName party={trx.counterparty} />}
        wordBreak="break-all"
      />
      <BeneficiaryItem trx={trx} />
      {category ? <DetailsListItem label={t('Category')} value={category} /> : null}
      {trx.description ? (
        <DetailsListItem label={t('Description')} value={trx.description} />
      ) : null}
      {transactionHash ? (
        <DetailsListItem label={t('Hash')} value={transactionHash} wordBreak="break-all" />
      ) : null}
      {link ? <BrightyListItem<'a'> title={t('Explorer')} href={link} target="_blank" /> : null}
      {address ? <DetailsListItem label={t('Address')} value={<Address {...address} />} /> : null}
    </List>
  );
}
